/**
 * @description : Modify booking workflow for flights. Continuation of a modify
 *                request raised in the customer-facing app. Renders an 8-state
 *                state machine inside TransactionList (full-page takeover).
 *                Drives every ops-facing endpoint documented in
 *                Modification-flow.docx v9 (SMB-2656 + SMB-2905 + SMB-2906 +
 *                SMB-2909).
 * @author: Azamuddin
 * @date: 05/21/2026
 */

//********IMPORTS STARTS*********/
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import {
    BackendStatus,
    ManualIssuanceSectorResult,
    ManualQuoteSector,
    SendQuoteSector,
    SendQuotePaxRate,
    ModState,
    ModifyStatusResponse,
    PaxRates,
    PaxType,
    Sector,
    cancelModify,
    checkAvailability,
    fetchFromSupplier,
    getModifyStatus,
    isTerminalStatus,
    manualIssuance,
    markIssuanceFailed,
    markRefunded,
    sendQuote,
    statusToModState,
    triggerIssuance,
} from "../operations/modifyBookingService";
// @ts-ignore -- authSession is JS
import { getUserDetails } from "../operations/authSession";
//********IMPORTS ENDS*********/

//********CONSTANTS STARTS*********/
const STEPS = ["Received", "Check supplier", "Review quote", "Send to customer", "Customer"];

const STATE_TO_STEP: Record<ModState, number> = {
    new_request: 0,
    checking: 1,
    quote_ready: 2,
    sent_to_customer: 3,
    customer_approved: 4,
    customer_rejected: 4,
    refund_pending: 4,
    refund_complete: 4,
};

const STATE_LABEL: Record<ModState, string> = {
    new_request: "Awaiting action",
    checking: "Checking supplier",
    quote_ready: "Quote ready",
    sent_to_customer: "Sent to customer",
    customer_approved: "Approved",
    customer_rejected: "Declined",
    refund_pending: "Refund pending",
    refund_complete: "Refund complete",
};

const STATE_CHIP_COLOR: Record<ModState, { bg: string; text: string }> = {
    new_request: { bg: "#EFF6FF", text: "#2563EB" },
    checking: { bg: "#FFFBEB", text: "#D97706" },
    quote_ready: { bg: "#F5F3FF", text: "#7C3AED" },
    sent_to_customer: { bg: "#ECFDF5", text: "#059669" },
    customer_approved: { bg: "#ECFDF5", text: "#059669" },
    customer_rejected: { bg: "#FEF2F2", text: "#DC2626" },
    refund_pending: { bg: "#FFFBEB", text: "#D97706" },
    refund_complete: { bg: "#ECFDF5", text: "#059669" },
};

// Auto-refresh cadence per backend status (doc section 5.2). Used only after
// ops accepts the inline opt-in prompt; we never poll silently.
const POLL_CADENCE_SECONDS: Partial<Record<BackendStatus, number>> = {
    IN_PROGRESS: 5,
    QUOTE_PENDING: 30,
    PAY_SUCCESS: 10, // transient post-payment / pre-issuance -> auto-progresses to ISSUING
    ISSUING: 30,
    ISSUED: 10, // transient -> auto-progresses to CONFIRMED
};

const PROMPT_DELAY_MS = 10_000;

const COLORS = {
    accent: "#4F46E5",
    accentBg: "#EEF2FF",
    border: "#E2E5EF",
    bg: "#F7F8FC",
    card: "#FFFFFF",
    text: "#141824",
    dim: "#6B7280",
    lgray: "#F4F5F9",
    green: "#059669",
    greenBg: "#ECFDF5",
    amber: "#D97706",
    amberBg: "#FFFBEB",
    red: "#DC2626",
    redBg: "#FEF2F2",
};
//********CONSTANTS ENDS*********/

//********INTERFACES STARTS*********/
interface ModifyBookingWorkflowProps {
    modifyTransactionId: string;
    itineraryId: string;
    customerName?: string;
    customerEmail?: string;
    onBack: () => void;
}

interface ToastState {
    open: boolean;
    severity: "error" | "success" | "info" | "warning";
    message: string;
    traceId?: string;
}

interface FormState {
    snapshot: ModifyStatusResponse | null;
    lastFetchedAt: number | null;
    isFetching: boolean;
    isSubmitting: boolean;
    fetchError: { message: string; traceId?: string } | null;
    toast: ToastState;
    confirmCancelOpen: boolean;
    confirmMarkFailedOpen: boolean;
}

type FormAction =
    | { type: "FETCH_START" }
    | { type: "FETCH_SUCCESS"; snapshot: ModifyStatusResponse }
    | { type: "FETCH_ERROR"; message: string; traceId?: string }
    | { type: "SUBMIT_START" }
    | { type: "SUBMIT_END" }
    | { type: "TOAST"; toast: Omit<ToastState, "open"> }
    | { type: "TOAST_CLOSE" }
    | { type: "TOGGLE_CONFIRM_CANCEL"; open: boolean }
    | { type: "TOGGLE_CONFIRM_MARK_FAILED"; open: boolean };
//********INTERFACES ENDS*********/

//********REDUCER STARTS*********/
const initialFormState: FormState = {
    snapshot: null,
    lastFetchedAt: null,
    isFetching: false,
    isSubmitting: false,
    fetchError: null,
    toast: { open: false, severity: "info", message: "" },
    confirmCancelOpen: false,
    confirmMarkFailedOpen: false,
};

function formReducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case "FETCH_START":
            return { ...state, isFetching: true, fetchError: null };
        case "FETCH_SUCCESS":
            return {
                ...state,
                isFetching: false,
                snapshot: action.snapshot,
                lastFetchedAt: Date.now(),
                fetchError: null,
            };
        case "FETCH_ERROR":
            return {
                ...state,
                isFetching: false,
                fetchError: { message: action.message, traceId: action.traceId },
            };
        case "SUBMIT_START":
            return { ...state, isSubmitting: true };
        case "SUBMIT_END":
            return { ...state, isSubmitting: false };
        case "TOAST":
            return { ...state, toast: { ...action.toast, open: true } };
        case "TOAST_CLOSE":
            return { ...state, toast: { ...state.toast, open: false } };
        case "TOGGLE_CONFIRM_CANCEL":
            return { ...state, confirmCancelOpen: action.open };
        case "TOGGLE_CONFIRM_MARK_FAILED":
            return { ...state, confirmMarkFailedOpen: action.open };
        default:
            return state;
    }
}
//********REDUCER ENDS*********/

//********HELPERS STARTS*********/
const formatMoney = (n: number | string | undefined): string => {
    const v = typeof n === "string" ? parseFloat(n) : n ?? 0;
    if (Number.isNaN(v)) return "$0.00";
    return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parse a raw numeric-input string to a number; empty / invalid -> 0. Numeric
// inputs are string-backed so they can be cleared and accept decimals mid-type.
const numOf = (s: string): number => {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
};

// /status may omit paxSummary, and passengers may be top-level OR nested per
// sector. Derive per-pax-type counts from whichever is present (per-sector uses
// the max count per type across sectors, since the same pax repeats per sector).
const effectivePaxSummary = (snapshot: ModifyStatusResponse): Partial<Record<PaxType, number>> => {
    if (snapshot.paxSummary && Object.keys(snapshot.paxSummary).length > 0) return snapshot.paxSummary;
    const counts: Partial<Record<PaxType, number>> = {};
    if (snapshot.passengers && snapshot.passengers.length > 0) {
        snapshot.passengers.forEach((p) => {
            if (p.paxType) counts[p.paxType] = (counts[p.paxType] ?? 0) + 1;
        });
    } else {
        // per-sector passengers: count per sector, keep the max per type
        snapshot.sectors.forEach((s) => {
            const perSector: Partial<Record<PaxType, number>> = {};
            (s.passengers || []).forEach((p) => {
                if (p.paxType) perSector[p.paxType] = (perSector[p.paxType] ?? 0) + 1;
            });
            (Object.keys(perSector) as PaxType[]).forEach((pt) => {
                counts[pt] = Math.max(counts[pt] ?? 0, perSector[pt] ?? 0);
            });
        });
    }
    return Object.keys(counts).length > 0 ? counts : { ADT: 1 };
};

const formatRelative = (ts: number | null): string => {
    if (!ts) return "never";
    const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
};

const formatDateLabel = (iso?: string): string => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
};

const formatExpiresIn = (iso?: string): string => {
    if (!iso) return "-";
    const ms = new Date(iso).getTime() - Date.now();
    if (Number.isNaN(ms)) return "-";
    if (ms <= 0) return "expired";
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remM = minutes % 60;
    return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`;
};

// The gateway can return an error as an HTTP 200 whose body carries the real
// status (axios does NOT throw), optionally wrapping the engine envelope under
// `rawError`. Unwrap before reading; treat a resolved body with statusCode >= 400
// (or a rawError wrapper) as an error.
function unwrapEnvelope(raw: any): any {
    return raw && typeof raw === "object" && raw.rawError && typeof raw.rawError === "object"
        ? raw.rawError
        : raw;
}
function errorEnvelopeOf(res: any): any | null {
    if (!res || typeof res !== "object") return null;
    if (res.rawError) return res;
    const sc = Number(res.statusCode ?? 0);
    return sc >= 400 ? res : null;
}
function statusOf(raw: any): number {
    const env = unwrapEnvelope(raw) || {};
    return Number(env.statusCode ?? raw?.statusCode ?? raw?.status ?? 0) || 0;
}
function refOf(raw: any): string | undefined {
    const env = unwrapEnvelope(raw) || {};
    return env.errorReference || raw?.errorReference;
}
function messageOf(raw: any): string | undefined {
    const env = unwrapEnvelope(raw) || {};
    return env.message || raw?.message;
}
function parseModifyError(err: unknown): { statusCode: number; traceId?: string; message?: string } {
    const e = err as { response?: { status?: number; data?: any; headers?: Record<string, string> } };
    const data = e?.response?.data;
    return {
        statusCode: statusOf(data) || e?.response?.status || 0,
        traceId: refOf(data) || e?.response?.headers?.["x-trace-id"],
        message: messageOf(data),
    };
}

// Our OWN error copy, by status code -- we never surface the raw service message.
// The traceId (a support code, not a sentence) is still shown for tracing.
const FRIENDLY_ERROR: Record<number, string> = {
    400: "We couldn't process that request. Please refresh and try again.",
    401: "Your session has expired. Please sign in again.",
    403: "You don't have permission to perform this action.",
    404: "This modification couldn't be found. Go back and refresh the list.",
    409: "This action isn't available for the booking's current status. Refreshed to the latest state.",
    422: "The modification details couldn't be validated. Please review the values and try again.",
    503: "The service is temporarily unavailable. Please try again in a moment.",
};
const friendlyError = (statusCode: number): string =>
    FRIENDLY_ERROR[statusCode] || "Something went wrong. Please try again.";

const autoRefreshKey = (txnId: string, modState: ModState) =>
    `modifyAutoRefresh:${txnId}:${modState}`;

const sectorIsManual = (s: Sector): boolean =>
    s.flowType === "MANUAL" || s.quoteSource === "MANUAL";

const getAgentId = (): string => {
    const u = getUserDetails();
    return u?.userId || u?.id || u?.email || "OPS_AGENT";
};
//********HELPERS ENDS*********/

//********PRESENTATIONAL STARTS*********/
const Stepper: React.FC<{ activeStep: number }> = ({ activeStep }) => (
    <Box sx={{ display: "flex", alignItems: "center", p: "12px 16px" }}>
        {STEPS.map((label, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            return (
                <React.Fragment key={label}>
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 10,
                            fontWeight: 700,
                            color: done || active ? "#fff" : COLORS.dim,
                            flexShrink: 0,
                            background: done ? COLORS.green : active ? COLORS.accent : "#E5E7EB",
                        }}
                    >
                        {done ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 11,
                            ml: "6px",
                            mr: "4px",
                            whiteSpace: "nowrap",
                            fontWeight: active ? 600 : 400,
                            color: active ? COLORS.text : COLORS.dim,
                        }}
                    >
                        {label}
                    </Typography>
                    {i < STEPS.length - 1 && (
                        <Box
                            sx={{
                                width: 24,
                                height: "1px",
                                flexShrink: 0,
                                background: done ? COLORS.green : "#E5E7EB",
                            }}
                        />
                    )}
                </React.Fragment>
            );
        })}
    </Box>
);

const DateCompareRow: React.FC<{ label: string; current: string; requested: string }> = ({
    label,
    current,
    requested,
}) => (
    <Box sx={{ mb: "14px" }}>
        <Typography
            sx={{
                fontSize: 10,
                fontWeight: 600,
                color: COLORS.dim,
                textTransform: "uppercase",
                letterSpacing: ".4px",
                mb: "6px",
            }}
        >
            {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
                sx={{
                    flex: 1,
                    p: "10px 14px",
                    borderRadius: 2,
                    background: COLORS.lgray,
                    border: `1px solid ${COLORS.border}`,
                }}
            >
                <Typography sx={{ fontSize: 10, color: COLORS.dim, display: "block", mb: "2px" }}>
                    Current
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{current}</Typography>
            </Box>
            <ArrowForwardIcon sx={{ color: COLORS.accent, fontSize: 18 }} />
            <Box
                sx={{
                    flex: 1,
                    p: "10px 14px",
                    borderRadius: 2,
                    background: COLORS.accentBg,
                    border: "1px solid rgba(79,70,229,.15)",
                }}
            >
                <Typography
                    sx={{
                        fontSize: 10,
                        color: COLORS.accent,
                        fontWeight: 600,
                        display: "block",
                        mb: "2px",
                    }}
                >
                    Requested
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.accent }}>
                    {requested}
                </Typography>
            </Box>
        </Box>
    </Box>
);

const BookingDetailCard: React.FC<{
    snapshot: ModifyStatusResponse;
    customerName?: string;
    customerEmail?: string;
}> = ({ snapshot, customerName, customerEmail }) => {
    const supplier = snapshot.sectors[0]?.supplierName || snapshot.sectors[0]?.supplier || "Supplier";
    const supplierConf = snapshot.supplierConfBefore || "-";
    const bookDate = formatDateLabel(snapshot.createdAt);

    return (
        <Paper
            elevation={0}
            sx={{
                p: "16px 20px",
                mb: "14px",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 3,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: "12px",
                }}
            >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Booking details</Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                    {supplier} ({supplierConf}) &middot; Booked {bookDate}
                </Typography>
            </Box>

            <Box
                sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: COLORS.accent,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    mb: "10px",
                }}
            >
                <CalendarMonthIcon sx={{ fontSize: 14 }} />
                <span>Change requested</span>
                <Box
                    component="span"
                    sx={{
                        fontSize: 9,
                        p: "1px 6px",
                        borderRadius: 1,
                        background: COLORS.amberBg,
                        color: COLORS.amber,
                        fontWeight: 600,
                    }}
                >
                    {snapshot.modifyType.replace(/_/g, " ").toLowerCase()}
                </Box>
            </Box>

            {snapshot.sectors.map((sector, idx) => (
                <DateCompareRow
                    key={sector.transactionId || idx}
                    label={`${sector.departure} -> ${sector.arrival}${
                        sector.supplierName ? ` (${sector.supplierName})` : ""
                    }`}
                    current={formatDateLabel(sector.originalDate)}
                    requested={formatDateLabel(sector.newDate)}
                />
            ))}

            <Box
                sx={{
                    p: "8px 14px",
                    borderRadius: 2,
                    background: COLORS.lgray,
                    border: `1px solid ${COLORS.border}`,
                    fontSize: 11,
                    mt: "6px",
                    display: "flex",
                    gap: "18px",
                    flexWrap: "wrap",
                    color: COLORS.dim,
                }}
            >
                {customerName && (
                    <span>
                        Customer:{" "}
                        <b style={{ color: COLORS.text }}>{customerName}</b>
                    </span>
                )}
                {customerEmail && (
                    <span>
                        Email:{" "}
                        <b style={{ color: COLORS.text }}>{customerEmail}</b>
                    </span>
                )}
                <span>
                    Booking:{" "}
                    <b style={{ color: COLORS.text }}>{snapshot.bookingId}</b>
                </span>
                {snapshot.customerTotal && (
                    <span>
                        Quote total:{" "}
                        <b style={{ color: COLORS.text }}>{formatMoney(snapshot.customerTotal)}</b>
                    </span>
                )}
            </Box>
        </Paper>
    );
};

const LoadingPanel: React.FC<{
    tint: "amber" | "accent";
    title: string;
    subtitle?: string;
}> = ({ tint, title, subtitle }) => {
    const palette =
        tint === "amber"
            ? { bg: COLORS.amberBg, color: COLORS.amber, border: "rgba(217,119,6,.12)" }
            : { bg: COLORS.accentBg, color: COLORS.accent, border: "rgba(79,70,229,.12)" };
    return (
        <Box
            sx={{
                p: "24px",
                textAlign: "center",
                borderRadius: 3,
                background: palette.bg,
                border: `1px solid ${palette.border}`,
            }}
        >
            <CircularProgress size={20} sx={{ color: palette.color, mb: "6px" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: palette.color }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: 12, color: COLORS.dim, mt: "3px" }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

interface AutoRefreshPromptProps {
    cadenceSeconds: number;
    onAccept: () => void;
    onDismiss: () => void;
}
const AutoRefreshPrompt: React.FC<AutoRefreshPromptProps> = ({
    cadenceSeconds,
    onAccept,
    onDismiss,
}) => (
    <Box
        sx={{
            p: "10px 14px",
            mb: "12px",
            borderRadius: 2,
            background: COLORS.accentBg,
            border: "1px solid rgba(79,70,229,.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
        }}
    >
        <Typography sx={{ fontSize: 12, color: COLORS.accent, fontWeight: 600 }}>
            Auto-refresh every {cadenceSeconds}s to see live updates from the supplier?
        </Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
                size="small"
                onClick={onAccept}
                sx={{
                    background: COLORS.accent,
                    color: "#fff",
                    fontSize: 11,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { background: "#3730a3" },
                }}
            >
                Yes, auto-refresh
            </Button>
            <Button
                size="small"
                onClick={onDismiss}
                sx={{
                    color: COLORS.dim,
                    fontSize: 11,
                    textTransform: "none",
                    fontWeight: 600,
                }}
            >
                No, I will refresh manually
            </Button>
        </Box>
    </Box>
);

const ActiveAutoRefreshBar: React.FC<{
    cadenceSeconds: number;
    onPause: () => void;
}> = ({ cadenceSeconds, onPause }) => (
    <Box
        sx={{
            p: "8px 14px",
            mb: "12px",
            borderRadius: 2,
            background: COLORS.greenBg,
            border: "1px solid rgba(5,150,105,.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
        }}
    >
        <Typography sx={{ fontSize: 11, color: COLORS.green, fontWeight: 600 }}>
            Auto-refreshing every {cadenceSeconds}s
        </Typography>
        <Button
            size="small"
            onClick={onPause}
            sx={{ color: COLORS.green, fontSize: 11, textTransform: "none", fontWeight: 600 }}
        >
            Pause
        </Button>
    </Box>
);
//********PRESENTATIONAL ENDS*********/

//********STEP VIEWS STARTS*********/
const StepCheckSupplier: React.FC<{
    supplier: string;
    onCheck: () => void;
    submitting: boolean;
}> = ({ supplier, onCheck, submitting }) => (
    <Paper
        elevation={0}
        sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}
    >
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: "3px" }}>
            Step 1: Check with {supplier}
        </Typography>
        <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "14px" }}>
            Query {supplier} for availability and pricing on the requested dates.
        </Typography>
        <Button
            variant="contained"
            startIcon={
                submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SearchIcon />
            }
            onClick={onCheck}
            disabled={submitting}
            sx={{
                background: COLORS.accent,
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 600,
            }}
        >
            Check availability &amp; pricing
        </Button>
    </Paper>
);

// Markup placement modes (Section 4 of the ops send-quote guide):
//   pct       -> top-level markupPct (always allowed)
//   amt       -> top-level flat markupAmt (shared-PNR, or split with <=1 priced)
//   perSector -> per-sector markupAmt on each priced sector (split, 2+ priced)
type MarkupMode = "pct" | "amt" | "perSector";

// Per-sector classification for the pricing panel: who prices it and whether ops
// may still enter rates. Inputs are enabled only for MANUAL sectors that are not
// yet terminal (availabilityStatus not in {AVAILABLE, UNAVAILABLE}).
const sectorRowState = (s: Sector) => {
    const manual = sectorIsManual(s);
    const unavailable = s.availabilityStatus === "UNAVAILABLE";
    const available = s.availabilityStatus === "AVAILABLE";
    const editable = manual && !unavailable && !available;
    return { manual, unavailable, available, editable };
};
const availabilityTone = (status?: string): { color: string; bg: string } => {
    if (status === "AVAILABLE") return { color: COLORS.green, bg: COLORS.greenBg };
    if (status === "UNAVAILABLE" || status === "ERROR")
        return { color: COLORS.red, bg: "rgba(220,38,38,.08)" };
    return { color: COLORS.amber, bg: COLORS.amberBg }; // PENDING / unknown
};

// Group sectors into quote units. A shared-PNR round-trip (tripType "2") is ONE
// unit -- the price belongs to the PNR, not the direction, so ops enters it once
// against the group's primary sector and the backend mirrors it to the siblings
// (Ops-SendQuote Scenario 2 / CHANGE 7). Split bookings (tripType "1") stay
// per-sector. Sectors are grouped by quoteGroupKey, falling back to shared PNR.
const quoteGroups = (
    snapshot: ModifyStatusResponse
): Array<{ primary: Sector; members: Sector[] }> => {
    if (snapshot.tripType !== "2") return snapshot.sectors.map((s) => ({ primary: s, members: [s] }));
    const byKey = new Map<string, Sector[]>();
    snapshot.sectors.forEach((s) => {
        const key = s.quoteGroupKey || s.pnr || s.transactionId;
        const arr = byKey.get(key);
        if (arr) arr.push(s);
        else byKey.set(key, [s]);
    });
    return Array.from(byKey.values()).map((members) => ({ primary: members[0], members }));
};
const groupRouteLabel = (members: Sector[]): string =>
    members.map((m) => m.route || `${m.departure} -> ${m.arrival}`).join("  /  ");

interface SectorRefusal {
    refused: boolean;
    reason: string;
}
interface SectorPricingPanelProps {
    snapshot: ModifyStatusResponse;
    paxSummary: Partial<Record<PaxType, number>>;
    rates: ManualQuoteSector[];
    onRatesChange: (next: ManualQuoteSector[]) => void;
    refusals: Record<string, SectorRefusal>;
    onRefusalsChange: (next: Record<string, SectorRefusal>) => void;
    sectorMarkup: Record<string, string>;
    onSectorMarkupChange: (next: Record<string, string>) => void;
    showPerSectorMarkup: boolean;
}
// One row per sector: route, supplier, quoteSource + availability badges, and the
// pricing affordance for the row's state. AUTO rows are read-only; UNAVAILABLE
// rows show a "NOT MODIFIED" badge; editable MANUAL rows get per-pax rate inputs
// plus (split bookings only) a refuse toggle and an optional per-sector markup.
const SectorPricingPanel: React.FC<SectorPricingPanelProps> = ({
    snapshot,
    paxSummary,
    rates,
    onRatesChange,
    refusals,
    onRefusalsChange,
    sectorMarkup,
    onSectorMarkupChange,
    showPerSectorMarkup,
}) => {
    const isSplit = snapshot.tripType === "1";
    const paxTypes = (Object.keys(paxSummary || {}) as PaxType[]).filter(
        (p) => (paxSummary[p] ?? 0) > 0
    );
    // Raw input strings keyed by `${txnId}:${paxType}:${field}` so fields can be
    // empty / accept decimals; the numeric payload (rates) is kept in sync.
    const [inputStr, setInputStr] = useState<Record<string, string>>({});
    const valueBySector = new Map(rates.map((v) => [v.transactionId, v]));

    const updateRate = (
        sectorTid: string,
        paxType: PaxType,
        field: keyof PaxRates,
        next: number
    ) => {
        const existing = valueBySector.get(sectorTid) || { transactionId: sectorTid, paxRates: {} };
        const paxRates = { ...(existing.paxRates || {}) };
        const current: PaxRates = paxRates[paxType] || { fareDiff: 0, changeFee: 0, tax: 0, totalDiff: 0 };
        const updated: PaxRates = { ...current, [field]: next };
        updated.totalDiff = updated.fareDiff + updated.changeFee + updated.tax;
        paxRates[paxType] = updated;
        const nextSector: ManualQuoteSector = { transactionId: sectorTid, paxRates };
        // keep an entry for every editable manual sector, replacing only this one
        const has = rates.some((s) => s.transactionId === sectorTid);
        onRatesChange(
            has
                ? rates.map((s) => (s.transactionId === sectorTid ? nextSector : s))
                : [...rates, nextSector]
        );
    };
    const setRefused = (tid: string, refused: boolean) =>
        onRefusalsChange({ ...refusals, [tid]: { refused, reason: refusals[tid]?.reason || "" } });
    const setReason = (tid: string, reason: string) =>
        onRefusalsChange({ ...refusals, [tid]: { refused: refusals[tid]?.refused ?? true, reason } });

    return (
        <Paper
            elevation={0}
            sx={{ p: "16px 20px", mb: "12px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}
        >
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: "3px" }}>Sectors</Typography>
            <Typography sx={{ fontSize: 11, color: COLORS.dim, mb: "12px" }}>
                {isSplit
                    ? "AUTO sectors are priced by the supplier. Enter rates for MANUAL sectors, or mark one the supplier refused as unavailable."
                    : "AUTO sectors are priced by the supplier. This booking shares one PNR across directions, so enter the MANUAL rate once - it covers the whole trip."}
            </Typography>
            {quoteGroups(snapshot).map((group) => {
                const sector = group.primary; // the PNR group's primary sector drives the entry
                const { manual, unavailable, available } = sectorRowState(sector);
                const tone = availabilityTone(sector.availabilityStatus);
                const rec = valueBySector.get(sector.transactionId);
                const refused = !!refusals[sector.transactionId]?.refused;
                const routeLabel = groupRouteLabel(group.members);
                return (
                    <Box
                        key={sector.transactionId}
                        sx={{
                            p: "10px 12px",
                            mb: "8px",
                            background: COLORS.card,
                            borderRadius: 2,
                            border: `1px solid ${COLORS.border}`,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mb: "8px" }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{routeLabel}</Typography>
                            {sector.supplierName && (
                                <Typography sx={{ fontSize: 11, color: COLORS.dim }}>{sector.supplierName}</Typography>
                            )}
                            <Box
                                component="span"
                                sx={{
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    px: "6px",
                                    py: "2px",
                                    borderRadius: 1,
                                    color: manual ? COLORS.amber : COLORS.accent,
                                    background: manual ? COLORS.amberBg : "rgba(37,99,235,.08)",
                                }}
                            >
                                {manual ? "MANUAL" : "AUTO"}
                            </Box>
                            {sector.availabilityStatus && (
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 9.5,
                                        fontWeight: 700,
                                        px: "6px",
                                        py: "2px",
                                        borderRadius: 1,
                                        color: tone.color,
                                        background: tone.bg,
                                    }}
                                >
                                    {sector.availabilityStatus}
                                </Box>
                            )}
                        </Box>
                        {sector.autoRescueReason && (
                            <Typography sx={{ fontSize: 10.5, color: COLORS.amber, mb: "6px" }}>
                                Auto-pricing failed ({sector.autoRescueReason}) - now priced manually.
                            </Typography>
                        )}

                        {unavailable ? (
                            <Box sx={{ p: "8px 10px", borderRadius: 1.5, background: COLORS.lgray }}>
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        px: "6px",
                                        py: "2px",
                                        borderRadius: 1,
                                        color: COLORS.red,
                                        background: "rgba(220,38,38,.08)",
                                    }}
                                >
                                    NOT MODIFIED - original ticket valid
                                </Box>
                                {sector.unavailableReason && (
                                    <Typography sx={{ fontSize: 11, color: COLORS.dim, mt: "6px" }}>
                                        {sector.unavailableReason}
                                    </Typography>
                                )}
                            </Box>
                        ) : !manual ? (
                            <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                                {available ? "Priced by supplier." : "Waiting for supplier..."}
                            </Typography>
                        ) : available ? (
                            <Typography sx={{ fontSize: 11, color: COLORS.green }}>Rates saved.</Typography>
                        ) : (
                            <>
                                {isSplit && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={refused}
                                                onChange={(e) => setRefused(sector.transactionId, e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: 11 }}>
                                                Supplier refused - mark this sector unavailable
                                            </Typography>
                                        }
                                        sx={{ mb: "4px" }}
                                    />
                                )}
                                {refused ? (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Reason shown to customer"
                                        placeholder="Airline does not permit reissue on this fare"
                                        value={refusals[sector.transactionId]?.reason || ""}
                                        onChange={(e) => setReason(sector.transactionId, e.target.value)}
                                    />
                                ) : (
                                    <>
                                        {paxTypes.map((paxType) => {
                                            const r = rec?.paxRates?.[paxType] || {
                                                fareDiff: 0,
                                                changeFee: 0,
                                                tax: 0,
                                                totalDiff: 0,
                                            };
                                            return (
                                                <Box
                                                    key={paxType}
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: "60px 1fr 1fr 1fr 1fr",
                                                        gap: "8px",
                                                        alignItems: "center",
                                                        mb: "6px",
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                                                        {paxType} x{paxSummary[paxType]}
                                                    </Typography>
                                                    {(["fareDiff", "changeFee", "tax"] as const).map((field) => {
                                                        const key = `${sector.transactionId}:${paxType}:${field}`;
                                                        return (
                                                            <TextField
                                                                key={field}
                                                                size="small"
                                                                type="number"
                                                                placeholder="0.00"
                                                                label={field}
                                                                value={inputStr[key] ?? ""}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    setInputStr((s) => ({ ...s, [key]: raw }));
                                                                    updateRate(sector.transactionId, paxType, field, numOf(raw));
                                                                }}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">$</InputAdornment>
                                                                    ),
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                    <Typography
                                                        sx={{ fontSize: 11, fontWeight: 600, fontFamily: "monospace" }}
                                                    >
                                                        Total {formatMoney(r.totalDiff)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                        {showPerSectorMarkup && (
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Sector markup"
                                                placeholder="0.00"
                                                value={sectorMarkup[sector.transactionId] || ""}
                                                onChange={(e) =>
                                                    onSectorMarkupChange({
                                                        ...sectorMarkup,
                                                        [sector.transactionId]: e.target.value,
                                                    })
                                                }
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                }}
                                                sx={{ mt: "4px", maxWidth: 180 }}
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Box>
                );
            })}
        </Paper>
    );
};

interface SendQuotePayload {
    rates: ManualQuoteSector[];
    refusals: Record<string, SectorRefusal>;
    sectorMarkup: Record<string, string>;
}
interface StepReviewQuoteProps {
    snapshot: ModifyStatusResponse;
    onSend: (markup: { markupPct?: number; markupAmt?: number }, payload?: SendQuotePayload) => void;
    submitting: boolean;
}
const StepReviewQuote: React.FC<StepReviewQuoteProps> = ({ snapshot, onSend, submitting }) => {
    // Per-sector quoteSource: AUTO sectors are priced by the backend (read-only);
    // MANUAL sectors need ops to enter the fare details. A booking can mix both,
    // and (split bookings only) ops can refuse a sector the supplier won't reissue.
    const isSplit = snapshot.tripType === "1";
    const paxSummary = effectivePaxSummary(snapshot);
    // Editable MANUAL quote units awaiting ops entry (AVAILABLE = already saved,
    // UNAVAILABLE = refused). One per quote group: a shared-PNR round-trip is a
    // single unit priced against its primary sector (not per direction).
    const editableManualSectors = quoteGroups(snapshot)
        .map((g) => g.primary)
        .filter((s) => sectorRowState(s).editable);

    const [markupValue, setMarkupValue] = useState<string>("");
    const [manualQuoteRows, setManualQuoteRows] = useState<ManualQuoteSector[]>(
        editableManualSectors.map((s) => ({ transactionId: s.transactionId, paxRates: {} }))
    );
    const [refusals, setRefusals] = useState<Record<string, SectorRefusal>>({});
    const [sectorMarkup, setSectorMarkup] = useState<Record<string, string>>({});

    // A sector is "priced" (carries a price in the quote) unless it is terminal-
    // refused (UNAVAILABLE) or ops just marked it refused. How many priced sectors
    // a split booking has drives where the markup goes (Section 4 of the ops guide).
    const isRefused = (s: Sector) =>
        s.availabilityStatus === "UNAVAILABLE" || !!refusals[s.transactionId]?.refused;
    const pricedSectors = snapshot.sectors.filter((s) => !isRefused(s));
    const pricedCount = isSplit ? pricedSectors.length : 1;
    // Per-sector flat markup is offered only when every priced sector is an
    // editable MANUAL row (an AUTO supplier price can't carry an ops flat amount).
    const perSectorAllowed =
        isSplit && pricedCount >= 2 && pricedSectors.every((s) => sectorRowState(s).editable);
    const markupModes: MarkupMode[] = !isSplit
        ? ["pct", "amt"]
        : pricedCount >= 2
          ? perSectorAllowed
              ? ["pct", "perSector"]
              : ["pct"]
          : ["pct", "amt"];
    const [markupMode, setMarkupMode] = useState<MarkupMode>("pct");
    const effectiveMode: MarkupMode = markupModes.includes(markupMode) ? markupMode : markupModes[0];

    // Supplier total = backend AUTO total + entered MANUAL sector totals (per-pax
    // totalDiff x pax count, skipping refused sectors). Approximate preview only --
    // the backend computes the authoritative customerPrice.
    const manualSupplierTotal = manualQuoteRows.reduce((sum, sec) => {
        if (refusals[sec.transactionId]?.refused) return sum;
        return (
            sum +
            (Object.keys(sec.paxRates) as PaxType[]).reduce((s, pt) => {
                const rate = sec.paxRates[pt];
                const count = paxSummary[pt] ?? 1;
                return s + (rate?.totalDiff ?? 0) * count;
            }, 0)
        );
    }, 0);
    const autoSupplierTotal = parseFloat(snapshot.supplierTotal || "0") || 0;
    const supplierTotal = autoSupplierTotal + manualSupplierTotal;
    const markupNum = numOf(markupValue);
    const sectorMarkupTotal = pricedSectors.reduce(
        (s, sec) => s + numOf(sectorMarkup[sec.transactionId] || ""),
        0
    );
    const markupAmount =
        effectiveMode === "pct"
            ? (supplierTotal * markupNum) / 100
            : effectiveMode === "perSector"
              ? sectorMarkupTotal
              : markupNum;
    // SMB-3206: the backend adds a config-based service fee on top of supplier +
    // markup. Surface it (when quoteDetails carries it) so the preview total matches.
    const systemServiceFee = snapshot.quoteDetails?.serviceFee ?? 0;
    const customerTotal = supplierTotal + markupAmount + systemServiceFee;
    const isZero = customerTotal === 0;

    const handleSend = () => {
        const markup: { markupPct?: number; markupAmt?: number } =
            effectiveMode === "pct"
                ? { markupPct: markupNum }
                : effectiveMode === "amt"
                  ? { markupAmt: markupNum }
                  : {}; // perSector: amounts ride on each sectorQuotes entry
        onSend(markup, {
            rates: manualQuoteRows,
            refusals,
            sectorMarkup: effectiveMode === "perSector" ? sectorMarkup : {},
        });
    };

    return (
        <>
            <SectorPricingPanel
                snapshot={snapshot}
                paxSummary={paxSummary}
                rates={manualQuoteRows}
                onRatesChange={setManualQuoteRows}
                refusals={refusals}
                onRefusalsChange={setRefusals}
                sectorMarkup={sectorMarkup}
                onSectorMarkupChange={setSectorMarkup}
                showPerSectorMarkup={effectiveMode === "perSector"}
            />
            <Paper
                elevation={0}
                sx={{
                    p: "16px 20px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 3,
                }}
            >
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: "3px" }}>
                    Step 2: Review &amp; adjust quote
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "14px" }}>
                    {editableManualSectors.length > 0
                        ? "After entering supplier rates above, adjust markup and send to customer."
                        : "Supplier returned pricing. Adjust markup if needed, then send to customer."}
                </Typography>

                <Box
                    sx={{
                        p: "12px 14px",
                        mb: "14px",
                        borderRadius: 2.5,
                        background: COLORS.lgray,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: COLORS.dim,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            mb: "8px",
                        }}
                    >
                        Supplier total (read-only)
                    </Typography>
                    {(snapshot.quoteDetails?.sectors || []).map((qs) => (
                        <Box
                            key={qs.transactionId}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 12,
                                p: "4px 0",
                            }}
                        >
                            <span>{qs.route}</span>
                            <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                                {formatMoney(qs.sectorTotal)}
                            </span>
                        </Box>
                    ))}
                    {/* Partial quote (split only): sectors the supplier refused keep
                        their original ticket and carry no price. The backend never
                        emits unavailableSectors on tripType 2, but gate explicitly. */}
                    {isSplit &&
                        (snapshot.quoteDetails?.unavailableSectors || []).map((us) => (
                        <Box key={us.transactionId} sx={{ p: "4px 0" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    fontSize: 12,
                                    gap: "8px",
                                }}
                            >
                                <span>{us.route}</span>
                                <Box
                                    component="span"
                                    sx={{ fontSize: 9.5, fontWeight: 700, px: "6px", py: "2px", borderRadius: 1, color: COLORS.dim, background: COLORS.border }}
                                >
                                    NOT MODIFIED
                                </Box>
                            </Box>
                            {us.unavailableReason && (
                                <Typography sx={{ fontSize: 11, color: COLORS.dim, mt: "2px" }}>
                                    {us.unavailableReason}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                                Your original ticket for this sector remains valid and unchanged.
                            </Typography>
                        </Box>
                    ))}
                    <Divider sx={{ my: "6px" }} />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 13,
                            fontWeight: 700,
                        }}
                    >
                        <span>Supplier total</span>
                        <span style={{ fontFamily: "monospace" }}>{formatMoney(supplierTotal)}</span>
                    </Box>
                </Box>

                <Box
                    sx={{
                        p: "12px 14px",
                        mb: "14px",
                        borderRadius: 2.5,
                        background: COLORS.lgray,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: COLORS.dim,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            mb: "8px",
                        }}
                    >
                        Your markup
                    </Typography>
                    <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <Select
                            size="small"
                            value={effectiveMode}
                            onChange={(e) => setMarkupMode(e.target.value as MarkupMode)}
                            sx={{ minWidth: 150 }}
                        >
                            {markupModes.includes("pct") && <MenuItem value="pct">Percentage</MenuItem>}
                            {markupModes.includes("amt") && <MenuItem value="amt">Flat amount</MenuItem>}
                            {markupModes.includes("perSector") && (
                                <MenuItem value="perSector">Per-sector amount</MenuItem>
                            )}
                        </Select>
                        {effectiveMode === "perSector" ? (
                            <Typography sx={{ fontSize: 11, color: COLORS.dim, flex: 1 }}>
                                Enter each sector's markup on its row above.
                            </Typography>
                        ) : (
                            <TextField
                                size="small"
                                type="number"
                                placeholder="0"
                                value={markupValue}
                                onChange={(e) => setMarkupValue(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {effectiveMode === "pct" ? "%" : "$"}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ flex: 1 }}
                            />
                        )}
                    </Box>
                    {isSplit && pricedCount >= 2 && !perSectorAllowed && (
                        <Typography sx={{ fontSize: 10.5, color: COLORS.dim, mt: "6px" }}>
                            This split booking has multiple priced sectors. Use a percentage - a
                            flat amount would have to be split across sectors by the system.
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        p: "16px 18px",
                        borderRadius: 2.5,
                        mb: "16px",
                        background: isZero ? COLORS.greenBg : COLORS.amberBg,
                        border: `1px solid ${
                            isZero ? "rgba(5,150,105,.15)" : "rgba(217,119,6,.15)"
                        }`,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: ".5px",
                            mb: "8px",
                            color: isZero ? COLORS.green : COLORS.amber,
                        }}
                    >
                        {isZero ? "No additional charges" : "Customer will see"}
                    </Typography>
                    {/* Partial modify (split): the total covers only the modifiable
                        sectors -- refused sectors keep their original ticket. */}
                    {isSplit &&
                        ((snapshot.quoteDetails?.unavailableSectors?.length ?? 0) > 0 ||
                            snapshot.sectors.some((s) => isRefused(s))) && (
                            <Typography sx={{ fontSize: 11, color: COLORS.dim, mb: "8px" }}>
                                This price applies only to the sector(s) that can be modified.
                                Refused sectors keep their original ticket.
                            </Typography>
                        )}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 12,
                            p: "4px 0",
                        }}
                    >
                        <span>Supplier total</span>
                        <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                            {formatMoney(supplierTotal)}
                        </span>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 12,
                            p: "4px 0",
                        }}
                    >
                        <span>Your markup</span>
                        <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                            {formatMoney(markupAmount)}
                        </span>
                    </Box>
                    {systemServiceFee > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, p: "4px 0" }}>
                            <span>Service fee (system)</span>
                            <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
                                {formatMoney(systemServiceFee)}
                            </span>
                        </Box>
                    )}
                    <Divider sx={{ borderStyle: "dashed", my: "6px" }} />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 15,
                            p: "8px 0 0",
                        }}
                    >
                        <span style={{ fontWeight: 700 }}>Total to customer</span>
                        <span
                            style={{
                                fontFamily: "monospace",
                                fontWeight: 800,
                                fontSize: 18,
                                color: isZero ? COLORS.green : COLORS.text,
                            }}
                        >
                            {formatMoney(customerTotal)}
                        </span>
                    </Box>
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                        submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SendIcon />
                    }
                    onClick={handleSend}
                    disabled={submitting}
                    sx={{
                        background: COLORS.accent,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 600,
                        py: "10px",
                    }}
                >
                    Send quote to customer
                </Button>
            </Paper>
        </>
    );
};

const StepSentToCustomer: React.FC<{ snapshot: ModifyStatusResponse }> = ({ snapshot }) => {
    // Partial split quote: mirror what the customer sees -- the refused sectors and
    // the price-scope note -- so ops keeps that context after the quote is sent.
    const unavailable = snapshot.quoteDetails?.unavailableSectors || [];
    const isPartial = snapshot.tripType === "1" && unavailable.length > 0;
    return (
    <Paper
        elevation={0}
        sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "14px" }}>
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: COLORS.greenBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CheckIcon sx={{ fontSize: 16, color: COLORS.green }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    Quote sent to customer
                </Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                    Waiting for customer response
                </Typography>
            </Box>
        </Box>

        <Box
            sx={{
                p: "10px 14px",
                borderRadius: 2,
                background: COLORS.amberBg,
                border: "1px solid rgba(217,119,6,.12)",
                fontSize: 11,
                color: COLORS.amber,
                fontWeight: 600,
                mb: "14px",
            }}
        >
            Quote expires in {formatExpiresIn(snapshot.expiresAt)} ({" "}
            {formatDateLabel(snapshot.expiresAt)} ).
        </Box>

        {isPartial && (
            <Box
                sx={{
                    p: "10px 14px",
                    borderRadius: 2,
                    background: COLORS.lgray,
                    border: `1px solid ${COLORS.border}`,
                    mb: "14px",
                }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 700, mb: "6px" }}>
                    Not modified - original ticket kept
                </Typography>
                {unavailable.map((us) => (
                    <Box key={us.transactionId} sx={{ mb: "6px" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, gap: "8px" }}>
                            <span>{us.route}</span>
                            <Box
                                component="span"
                                sx={{ fontSize: 9.5, fontWeight: 700, px: "6px", py: "2px", borderRadius: 1, color: COLORS.dim, background: COLORS.border }}
                            >
                                NOT MODIFIED
                            </Box>
                        </Box>
                        {us.unavailableReason && (
                            <Typography sx={{ fontSize: 11, color: COLORS.dim, mt: "2px" }}>
                                {us.unavailableReason}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                            Your original ticket for this sector remains valid and unchanged.
                        </Typography>
                    </Box>
                ))}
            </Box>
        )}

        <Box
            sx={{
                p: "12px 14px",
                borderRadius: 2,
                background: COLORS.lgray,
                border: `1px solid ${COLORS.border}`,
            }}
        >
            {isPartial && (
                <Typography sx={{ fontSize: 11, color: COLORS.dim, mb: "8px" }}>
                    This price applies only to the sector(s) that can be modified.
                </Typography>
            )}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    p: "3px 0",
                }}
            >
                <span>Customer total</span>
                <span style={{ fontFamily: "monospace", fontWeight: 700 }}>
                    {formatMoney(snapshot.customerTotal)}
                </span>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    p: "3px 0",
                }}
            >
                <span style={{ color: COLORS.dim }}>Your markup</span>
                <span style={{ fontFamily: "monospace", fontWeight: 700, color: COLORS.green }}>
                    {formatMoney(snapshot.markup)}
                </span>
            </Box>
        </Box>

        <Typography sx={{ fontSize: 11, color: COLORS.dim, mt: "12px" }}>
            The customer approves or declines from the MyTrip app. Refresh to see updates.
        </Typography>
    </Paper>
    );
};

// Pax rows for a sector's reissue (per-sector passengers, else top-level, else
// expand from paxSummary).
const sectorPaxRows = (
    snapshot: ModifyStatusResponse,
    sector: Sector
): Array<{ paxType: PaxType; firstName?: string; lastName?: string }> => {
    // Top-level passengers[] carry a transactionId on split bookings; filter to
    // this sector so a multi-sector split with uneven pax-per-sector counts does
    // not replicate the whole booking's pax list onto every sector's form.
    const topLevel = snapshot.passengers && snapshot.passengers.length
        ? (snapshot.passengers.some((p) => p.transactionId)
              ? snapshot.passengers.filter((p) => p.transactionId === sector.transactionId)
              : snapshot.passengers)
        : [];
    const src =
        sector.passengers && sector.passengers.length
            ? sector.passengers
            : topLevel.length
              ? topLevel
              : null;
    if (src) return src.map((p) => ({ paxType: (p.paxType || "ADT") as PaxType, firstName: p.firstName, lastName: p.lastName }));
    const ps = effectivePaxSummary(snapshot);
    const rows: Array<{ paxType: PaxType }> = [];
    (Object.keys(ps) as PaxType[]).forEach((pt) => {
        for (let i = 0; i < (ps[pt] ?? 0); i++) rows.push({ paxType: pt });
    });
    return rows.length ? rows : [{ paxType: "ADT" }];
};

// Per-sector manual reissue entry. tripType "2" (shared PNR) = ONE entry (backend
// mirrors to siblings); tripType "1" (split) = one entry per priced sector. Refused
// (NOT_MODIFIED / UNAVAILABLE) sectors are excluded -- they keep the original ticket.
interface IssuanceRow {
    newPnr: string;
    newFareBasis: string;
    tickets: Array<{ firstName: string; lastName: string; paxType: PaxType; ticketNumber: string }>;
}
const ManualIssuanceForm: React.FC<{
    snapshot: ModifyStatusResponse;
    submitting: boolean;
    onSubmit: (results: ManualIssuanceSectorResult[]) => void;
    onFetchFromSupplier: () => void;
}> = ({ snapshot, submitting, onSubmit, onFetchFromSupplier }) => {
    const issuable = snapshot.sectors.filter(
        (s) =>
            sectorIsManual(s) &&
            s.issuanceStatus !== "NOT_MODIFIED" &&
            s.availabilityStatus !== "UNAVAILABLE"
    );
    const sharedPnr = snapshot.tripType === "2";
    // shared PNR: enter once against the first issuable sector
    const formSectors = sharedPnr ? issuable.slice(0, 1) : issuable;

    const [rows, setRows] = useState<Record<string, IssuanceRow>>(() => {
        const init: Record<string, IssuanceRow> = {};
        formSectors.forEach((s) => {
            init[s.transactionId] = {
                newPnr: "",
                newFareBasis: "",
                tickets: sectorPaxRows(snapshot, s).map((p) => ({
                    firstName: p.firstName || "",
                    lastName: p.lastName || "",
                    paxType: p.paxType,
                    ticketNumber: "",
                })),
            };
        });
        return init;
    });

    const setRow = (txnId: string, patch: Partial<IssuanceRow>) =>
        setRows((prev) => ({ ...prev, [txnId]: { ...prev[txnId], ...patch } }));
    const setTicket = (txnId: string, idx: number, field: "firstName" | "lastName" | "ticketNumber", v: string) =>
        setRows((prev) => ({
            ...prev,
            [txnId]: {
                ...prev[txnId],
                tickets: prev[txnId].tickets.map((t, i) => (i === idx ? { ...t, [field]: v } : t)),
            },
        }));

    const complete = formSectors.every((s) => {
        const r = rows[s.transactionId];
        return r && r.newPnr.trim() && r.tickets.every((t) => t.ticketNumber.trim());
    });

    const submit = () => {
        if (!complete) return;
        const results: ManualIssuanceSectorResult[] = formSectors.map((s) => {
            const r = rows[s.transactionId];
            const tickets = r.tickets.map((t) => ({
                firstName: t.firstName || undefined,
                lastName: t.lastName || undefined,
                paxType: t.paxType,
                ticketNumber: t.ticketNumber.trim(),
            }));
            const result: ManualIssuanceSectorResult = { transactionId: s.transactionId, newPnr: r.newPnr.trim() };
            if (tickets.length === 1) result.newTicketNumber = tickets[0].ticketNumber;
            else result.tickets = tickets;
            if (r.newFareBasis.trim()) result.newFareBasis = r.newFareBasis.trim();
            return result;
        });
        onSubmit(results);
    };

    return (
        <Paper elevation={0} sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: "3px" }}>Enter supplier reissue details (MANUAL)</Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "14px" }}>
                {sharedPnr
                    ? "Shared PNR: enter the new PNR + ticket per passenger once; it applies to both directions."
                    : "Each sector has its own PNR. Enter the new PNR + ticket per passenger for each sector."}
            </Typography>

            {formSectors.map((s) => {
                const r = rows[s.transactionId];
                return (
                    <Box key={s.transactionId} sx={{ p: "10px 12px", mb: "10px", borderRadius: 2, border: `1px solid ${COLORS.border}`, background: COLORS.lgray }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, mb: "8px" }}>
                            {sharedPnr ? "Shared PNR (all sectors)" : `${s.departure} -> ${s.arrival}`}
                            {s.supplierName ? ` - ${s.supplierName}` : ""}
                        </Typography>
                        <Box sx={{ display: "flex", gap: "8px", mb: "8px" }}>
                            <TextField size="small" label="New PNR" value={r.newPnr} onChange={(e) => setRow(s.transactionId, { newPnr: e.target.value })} sx={{ flex: 1 }} />
                            <TextField size="small" label="Fare basis (optional)" value={r.newFareBasis} onChange={(e) => setRow(s.transactionId, { newFareBasis: e.target.value })} sx={{ flex: 1 }} />
                        </Box>
                        {r.tickets.map((t, idx) => (
                            <Box key={idx} sx={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr 1fr", gap: "8px", mb: "6px", alignItems: "center" }}>
                                <Typography sx={{ fontSize: 11, color: COLORS.dim, fontWeight: 600 }}>{t.paxType}</Typography>
                                <TextField size="small" label="First name" value={t.firstName} onChange={(e) => setTicket(s.transactionId, idx, "firstName", e.target.value)} />
                                <TextField size="small" label="Last name" value={t.lastName} onChange={(e) => setTicket(s.transactionId, idx, "lastName", e.target.value)} />
                                <TextField size="small" label="Ticket #" value={t.ticketNumber} onChange={(e) => setTicket(s.transactionId, idx, "ticketNumber", e.target.value)} />
                            </Box>
                        ))}
                    </Box>
                );
            })}

            <Box sx={{ display: "flex", gap: "8px", mt: "8px" }}>
                <Button size="small" variant="outlined" onClick={onFetchFromSupplier} disabled={submitting} sx={{ textTransform: "none", borderRadius: 2 }}>
                    Fetch from supplier
                </Button>
                <Button size="small" variant="contained" onClick={submit} disabled={submitting || !complete} sx={{ ml: "auto", textTransform: "none", background: COLORS.accent, borderRadius: 2 }}>
                    {snapshot.status === "ISSUED" ? "Retry apply" : "Submit reissue"}
                </Button>
            </Box>
        </Paper>
    );
};

const StepCustomerApproved: React.FC<{
    snapshot: ModifyStatusResponse;
    submitting: boolean;
    onManualIssuance: (results: ManualIssuanceSectorResult[]) => void;
    onFetchFromSupplier: () => void;
}> = ({ snapshot, submitting, onManualIssuance, onFetchFromSupplier }) => {

    if (snapshot.status === "PAY_PENDING") {
        // A failed/abandoned attempt while still PAY_PENDING: the customer retries
        // (or restarts) on Stripe's hosted page. Ops can't re-open that page; the
        // only ops affordance here is Cancel modification (in the header).
        if (snapshot.paymentStatus === "FAILED") {
            return (
                <Paper
                    elevation={0}
                    sx={{ p: "16px 20px", border: `1px solid rgba(220,38,38,.25)`, borderRadius: 3, background: "rgba(220,38,38,.04)" }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.red, mb: "4px" }}>
                        Payment didn't go through
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "8px" }}>
                        {snapshot.paymentFailureReason || "The customer's payment attempt did not complete."}
                    </Typography>
                    {typeof snapshot.paymentFailureCount === "number" && snapshot.paymentFailureCount > 0 && (
                        <Typography sx={{ fontSize: 11, color: COLORS.dim, mb: "8px" }}>
                            Failed attempts: {snapshot.paymentFailureCount} of 5.
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                        The customer can retry on the Stripe page. After 5 failures the payment
                        session closes and the request expires. Use Cancel modification if they
                        want to stop.
                    </Typography>
                </Paper>
            );
        }
        return (
            <LoadingPanel
                tint="accent"
                title="Awaiting Stripe payment"
                subtitle={
                    snapshot.paymentSessionId
                        ? `Session: ${snapshot.paymentSessionId}`
                        : "Customer is on the Stripe checkout page"
                }
            />
        );
    }

    if (snapshot.status === "PAY_SUCCESS") {
        return (
            <LoadingPanel
                tint="accent"
                title="Payment received - starting supplier reissue..."
                subtitle="Stripe confirmed the payment. Issuance is being triggered; this advances to issuing shortly."
            />
        );
    }

    if (snapshot.status === "CONFIRMED" || snapshot.status === "COMPLETE") {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: "24px",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 3,
                    textAlign: "center",
                }}
            >
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: COLORS.green,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: "12px",
                    }}
                >
                    <CheckIcon sx={{ color: "#fff", fontSize: 26 }} />
                </Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, mb: "6px" }}>
                    Modification confirmed
                </Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "12px" }}>
                    Total paid {formatMoney(snapshot.customerTotal)}
                </Typography>
                {/* Per-sector: modified sectors carry their own newPnr + per-pax
                    e-tickets (allTickets[], falling back to newTicketNumber). A
                    NOT_MODIFIED sector kept its original ticket (partial modify). */}
                <Box sx={{ textAlign: "left", maxWidth: 520, mx: "auto" }}>
                    {snapshot.sectors.map((s) => {
                        const notModified = s.issuanceStatus === "NOT_MODIFIED";
                        const routeLabel = s.route || `${s.departure} -> ${s.arrival}`;
                        const tickets =
                            s.allTickets && s.allTickets.length
                                ? s.allTickets
                                : s.newTicketNumber
                                  ? [{ ticketNumber: s.newTicketNumber }]
                                  : [];
                        return (
                            <Box
                                key={s.transactionId}
                                sx={{
                                    p: "10px 12px",
                                    mb: "6px",
                                    borderRadius: 2,
                                    background: COLORS.lgray,
                                    border: `1px solid ${COLORS.border}`,
                                    fontSize: 12,
                                }}
                            >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                    <b>{routeLabel}</b>
                                    {notModified ? (
                                        <Box
                                            component="span"
                                            sx={{ fontSize: 10, fontWeight: 700, px: "6px", py: "2px", borderRadius: 1, color: COLORS.dim, background: COLORS.border }}
                                        >
                                            NOT MODIFIED
                                        </Box>
                                    ) : (
                                        s.newDate && (
                                            <span style={{ color: COLORS.dim }}>{formatDateLabel(s.newDate)}</span>
                                        )
                                    )}
                                </Box>
                                {notModified ? (
                                    <Typography sx={{ fontSize: 11, color: COLORS.dim, mt: "4px" }}>
                                        Original ticket valid{s.oldPnr || s.pnr ? ` - PNR ${s.oldPnr || s.pnr}` : ""}
                                        {s.unavailableReason ? `. ${s.unavailableReason}` : "."}
                                    </Typography>
                                ) : (
                                    <Box sx={{ mt: "4px" }}>
                                        <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                                            New PNR {s.newPnr || "-"}
                                        </Typography>
                                        {tickets.map((t, i) => (
                                            <Typography key={i} sx={{ fontSize: 11, fontFamily: "monospace" }}>
                                                {[t.paxType, t.firstName, t.lastName].filter(Boolean).join(" ")}
                                                {[t.paxType, t.firstName, t.lastName].some(Boolean) ? " - " : ""}
                                                {t.ticketNumber}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Paper>
        );
    }

    if (snapshot.status === "ISSUING" || snapshot.status === "ISSUED") {
        const isManualFlow = snapshot.sectors.some(sectorIsManual);
        if (isManualFlow) {
            return (
                <ManualIssuanceForm
                    snapshot={snapshot}
                    submitting={submitting}
                    onSubmit={onManualIssuance}
                    onFetchFromSupplier={onFetchFromSupplier}
                />
            );
        }
        return (
            <LoadingPanel
                tint="accent"
                title={
                    snapshot.status === "ISSUED"
                        ? "Tickets issued - finalizing confirmation..."
                        : "Reissuing with supplier..."
                }
                subtitle={
                    snapshot.status === "ISSUED"
                        ? "The supplier issued the new tickets. Applying them to the booking; this completes shortly."
                        : "Supplier API is processing. Updates land via auto-refresh or Refresh button."
                }
            />
        );
    }

    return <LoadingPanel tint="accent" title="Processing..." />;
};

const StepCustomerRejected: React.FC<{
    snapshot: ModifyStatusResponse;
    onStartNew: () => void;
}> = ({ snapshot, onStartNew }) => {
    // The airline refusing every sector auto-cancels the request with a specific
    // reason -- give it its own copy rather than the generic "did not proceed".
    const supplierUnavailable = snapshot.cancellationReason === "SUPPLIER_UNAVAILABLE";
    return (
        <Paper
            elevation={0}
            sx={{
                p: "32px 24px",
                textAlign: "center",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 3,
            }}
        >
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    mx: "auto",
                    mb: "12px",
                    background: COLORS.red,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CloseIcon sx={{ color: "#fff", fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: "6px" }}>
                {supplierUnavailable
                    ? "The airline could not fulfil this modification"
                    : "Modify did not proceed"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "16px" }}>
                {supplierUnavailable
                    ? "The original booking is unchanged."
                    : `Reason: ${snapshot.cancellationReason || snapshot.status}. Original booking is unchanged.`}
            </Typography>
            <Button
                variant="outlined"
                onClick={onStartNew}
                sx={{ textTransform: "none", borderRadius: 2 }}
            >
                Start a new modification
            </Button>
        </Paper>
    );
};

interface StepRefundPendingProps {
    snapshot: ModifyStatusResponse;
    submitting: boolean;
    onTriggerIssuance: () => void;
    onFetchFromSupplier: () => void;
    onMarkRefunded: (refundMethod: string, refundReference: string) => void;
    onOpenMarkFailed: () => void;
}
const StepRefundPending: React.FC<StepRefundPendingProps> = ({
    snapshot,
    submitting,
    onTriggerIssuance,
    onFetchFromSupplier,
    onMarkRefunded,
    onOpenMarkFailed,
}) => {
    const [refundMethod, setRefundMethod] = useState("");
    const [refundReference, setRefundReference] = useState("");
    const inGrace = snapshot.status === "ISSUANCE_FAILED";

    return (
        <Paper
            elevation={0}
            sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}
        >
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: "3px" }}>
                {inGrace ? "Issuance failed - 4h grace window" : "Refund pending"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "14px" }}>
                {snapshot.failureReason ||
                    snapshot.issuanceError ||
                    snapshot.cancellationReason ||
                    "Issuance did not complete; refund is pending accounts action."}
            </Typography>

            {inGrace && (
                <Box
                    sx={{
                        p: "10px 14px",
                        mb: "12px",
                        borderRadius: 2,
                        background: COLORS.amberBg,
                        border: "1px solid rgba(217,119,6,.15)",
                        fontSize: 11,
                        color: COLORS.amber,
                        fontWeight: 600,
                    }}
                >
                    Grace expires in {formatExpiresIn(snapshot.expiresAt)}. After that, the system
                    parks the row at REFUND_PEND automatically.
                </Box>
            )}

            <Typography sx={{ fontSize: 11, fontWeight: 600, color: COLORS.dim, mb: "8px" }}>
                OPS RECOVERY (within grace only)
            </Typography>
            <Box sx={{ display: "flex", gap: "8px", mb: "16px", flexWrap: "wrap" }}>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={submitting || !inGrace}
                    onClick={onTriggerIssuance}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    Retry issuance
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={submitting || !inGrace}
                    onClick={onFetchFromSupplier}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    Fetch from supplier
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={submitting}
                    onClick={onOpenMarkFailed}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        color: COLORS.red,
                        borderColor: COLORS.red,
                    }}
                >
                    Fast-path: mark issuance failed
                </Button>
            </Box>

            <Divider sx={{ my: "12px" }} />

            <Typography sx={{ fontSize: 11, fontWeight: 600, color: COLORS.dim, mb: "8px" }}>
                ACCOUNTS - MARK REFUND PROCESSED
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", mb: "12px" }}>
                <TextField
                    size="small"
                    label="Refund method"
                    placeholder="Visa ending 1234"
                    value={refundMethod}
                    onChange={(e) => setRefundMethod(e.target.value)}
                />
                <TextField
                    size="small"
                    label="Refund reference"
                    placeholder="STRP-pi_abc"
                    value={refundReference}
                    onChange={(e) => setRefundReference(e.target.value)}
                />
            </Box>
            <Button
                fullWidth
                variant="contained"
                disabled={submitting}
                onClick={() => onMarkRefunded(refundMethod, refundReference)}
                sx={{
                    background: COLORS.green,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2.5,
                    py: "10px",
                    "&:hover": { background: "#047857" },
                }}
            >
                Mark refund processed
            </Button>
            <Typography sx={{ fontSize: 10, color: COLORS.dim, mt: "6px", textAlign: "center" }}>
                Refund amount {formatMoney(snapshot.customerTotal || snapshot.refundAmount)}.
                Sends the refund-confirmation email to the customer.
            </Typography>
        </Paper>
    );
};

const StepRefundComplete: React.FC<{ snapshot: ModifyStatusResponse }> = ({ snapshot }) => (
    <Paper
        elevation={0}
        sx={{
            p: "32px 24px",
            textAlign: "center",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 3,
        }}
    >
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                mx: "auto",
                mb: "12px",
                background: COLORS.green,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <CheckIcon sx={{ color: "#fff", fontSize: 26 }} />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700, mb: "6px" }}>
            Refund complete
        </Typography>
        <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "8px" }}>
            {formatMoney(snapshot.customerTotal || snapshot.refundAmount)} refunded
            {snapshot.refundMethod ? ` via ${snapshot.refundMethod}` : ""}.
        </Typography>
        {snapshot.refundReference && (
            <Typography sx={{ fontSize: 11, color: COLORS.dim, fontFamily: "monospace" }}>
                Ref: {snapshot.refundReference}
            </Typography>
        )}
    </Paper>
);
//********STEP VIEWS ENDS*********/

//********MAIN COMPONENT STARTS*********/
const ModifyBookingWorkflow: React.FC<ModifyBookingWorkflowProps> = ({
    modifyTransactionId,
    itineraryId,
    customerName,
    customerEmail,
    onBack,
}) => {
    //********INITIATE STATE STARTS*********/
    const [state, dispatch] = useReducer(formReducer, initialFormState);
    const [autoRefreshDecided, setAutoRefreshDecided] = useState<"yes" | "no" | null>(null);
    const [autoRefreshActive, setAutoRefreshActive] = useState(false);
    const [showAutoRefreshPrompt, setShowAutoRefreshPrompt] = useState(false);
    const pollTimerRef = useRef<number | null>(null);
    const promptTimerRef = useRef<number | null>(null);
    //********INITIATE STATE ENDS*********/

    const snapshot = state.snapshot;
    const modState: ModState = snapshot ? statusToModState(snapshot.status) : "new_request";
    // Suppress auto-refresh when QUOTE_PENDING is fully MANUAL (ops entry, no
    // supplier polling). A mixed booking still has AUTO sectors being polled, so
    // keep the cadence there.
    const allManualQuotePending =
        snapshot?.status === "QUOTE_PENDING" &&
        snapshot.sectors.length > 0 &&
        snapshot.sectors.every(sectorIsManual);
    const cadence: number | undefined =
        snapshot && !allManualQuotePending
            ? POLL_CADENCE_SECONDS[snapshot.status]
            : undefined;
    const isTerminal = snapshot ? isTerminalStatus(snapshot.status) : false;

    //********HANDLERS STARTS*********/
    const refresh = useCallback(async () => {
        dispatch({ type: "FETCH_START" });
        try {
            const res = await getModifyStatus(modifyTransactionId);
            const errEnv = errorEnvelopeOf(res);
            if (errEnv) {
                const msg = friendlyError(statusOf(errEnv));
                dispatch({ type: "FETCH_ERROR", message: msg, traceId: refOf(errEnv) });
                dispatch({ type: "TOAST", toast: { severity: "error", message: msg, traceId: refOf(errEnv) } });
                return;
            }
            dispatch({ type: "FETCH_SUCCESS", snapshot: res.data });
        } catch (err) {
            const p = parseModifyError(err);
            const msg = friendlyError(p.statusCode);
            dispatch({ type: "FETCH_ERROR", message: msg, traceId: p.traceId });
            dispatch({ type: "TOAST", toast: { severity: "error", message: msg, traceId: p.traceId } });
        }
    }, [modifyTransactionId]);

    const submitAction = useCallback(
        async (
            fn: () => Promise<any>,
            successMessage: string,
            opts?: { verbatim409?: boolean }
        ) => {
            dispatch({ type: "SUBMIT_START" });
            // We normally surface our OWN copy by status code. On /send-quote, the
            // backend's 409 is informational (it names the blocking sector, and the
            // rates the agent typed were saved) -- show it verbatim. An all-refused
            // submission answers 409 "currently CANCELLED": that is the SUCCESS
            // outcome (auto-cancel done), not a failure. Any 409 re-fetches truth.
            const showError = async (statusCode: number, traceId?: string, message?: string) => {
                if (statusCode === 409) {
                    if (opts?.verbatim409 && message && /currently\s+CANCELLED/i.test(message)) {
                        dispatch({
                            type: "TOAST",
                            toast: {
                                severity: "success",
                                message:
                                    "Modification cancelled - customer notified. The customer has been emailed; no further action is needed.",
                            },
                        });
                    } else if (opts?.verbatim409 && message) {
                        // The QUOTE_PENDING/QUOTE_READY waiting 409 keeps everything sent
                        // ("nothing you send is ever lost"). Placement/shape 409s (markup
                        // rules, partial-on-shared-PNR) save nothing -- show those verbatim
                        // WITHOUT the (false) saved-rates reassurance.
                        const ratesSaved = /un-priced|must be in|QUOTE_PENDING|QUOTE_READY/i.test(message);
                        dispatch({
                            type: "TOAST",
                            toast: {
                                severity: "info",
                                message: ratesSaved
                                    ? `${message}  (Your entered rates were saved.)`
                                    : message,
                                traceId,
                            },
                        });
                    } else {
                        dispatch({ type: "TOAST", toast: { severity: "info", message: friendlyError(409), traceId } });
                    }
                    await refresh();
                } else {
                    dispatch({ type: "TOAST", toast: { severity: "error", message: friendlyError(statusCode), traceId } });
                }
            };
            try {
                const res = await fn();
                // A resolved response can still be a failure (gateway returns some
                // errors as HTTP 200 with the real status in the body).
                const errEnv = errorEnvelopeOf(res);
                if (errEnv) {
                    await showError(statusOf(errEnv), refOf(errEnv), messageOf(errEnv));
                    return;
                }
                // Best-effort customer email on /send-quote: warn if SES failed.
                const d = res?.data || {};
                if (d.emailSent === false) {
                    dispatch({
                        type: "TOAST",
                        toast: { severity: "warning", message: `${successMessage}, but the customer was NOT emailed: ${d.emailError || "email delivery failed"}` },
                    });
                } else {
                    dispatch({ type: "TOAST", toast: { severity: "success", message: successMessage } });
                }
                await refresh();
            } catch (err) {
                const p = parseModifyError(err);
                await showError(p.statusCode, p.traceId, p.message);
            } finally {
                dispatch({ type: "SUBMIT_END" });
            }
        },
        [refresh]
    );

    const handleCheckAvailability = () =>
        submitAction(
            () => checkAvailability({ modifyTransactionId, itineraryId }),
            "Supplier check started"
        );

    const handleSendQuote = (
        markup: { markupPct?: number; markupAmt?: number },
        payload?: SendQuotePayload
    ) => {
        // Backend expects a top-level sectorQuotes[], one entry per MANUAL sector
        // being priced or refused. Priced entries carry per-pax fareDiff/changeFee/
        // tax (NO totalDiff -- backend derives it) and, on a split booking with the
        // per-sector markup mode, a markupAmt. Refused entries (split only) carry
        // available:false + a customer-readable unavailableReason. AUTO sectors are
        // never sent (the supplier's price can't be overwritten).
        const sectorQuotes: SendQuoteSector[] = (payload?.rates || []).map((sec) => {
            const refusal = payload?.refusals?.[sec.transactionId];
            if (refusal?.refused) {
                return {
                    transactionId: sec.transactionId,
                    available: false,
                    unavailableReason:
                        refusal.reason.trim() || "Supplier cannot fulfil this modification",
                };
            }
            const paxRates: Partial<Record<PaxType, SendQuotePaxRate>> = {};
            (Object.keys(sec.paxRates) as PaxType[]).forEach((pt) => {
                const r = sec.paxRates[pt];
                if (r) paxRates[pt] = { fareDiff: r.fareDiff, changeFee: r.changeFee, tax: r.tax };
            });
            const entry: SendQuoteSector = { transactionId: sec.transactionId, paxRates };
            // Honor an explicit per-sector markup including 0; a blank field is left
            // out so the backend's auto-markup configuration applies instead.
            const rawMarkup = payload?.sectorMarkup?.[sec.transactionId];
            if (rawMarkup !== undefined && rawMarkup.trim() !== "") entry.markupAmt = numOf(rawMarkup);
            return entry;
        });
        return submitAction(
            () =>
                sendQuote({
                    modifyTransactionId,
                    agentId: getAgentId(),
                    ...markup,
                    ...(sectorQuotes.length ? { sectorQuotes } : {}),
                }),
            "Quote sent to customer",
            { verbatim409: true }
        );
    };

    const handleTriggerIssuance = () =>
        submitAction(
            () => triggerIssuance({ modifyTransactionId, itineraryId }),
            "Issuance retry started"
        );

    const handleManualIssuance = (sectorResults: ManualIssuanceSectorResult[]) =>
        submitAction(
            () =>
                manualIssuance({
                    modifyTransactionId,
                    agentId: getAgentId(),
                    sectorResults,
                }),
            "Reissue details submitted"
        );

    const handleFetchFromSupplier = () =>
        submitAction(
            () =>
                fetchFromSupplier({
                    modifyTransactionId,
                    itineraryId,
                    agentId: getAgentId(),
                }),
            "Fetched latest from supplier"
        );

    const handleMarkRefunded = (refundMethod: string, refundReference: string) =>
        submitAction(
            () =>
                markRefunded({
                    modifyTransactionId,
                    itineraryId,
                    refund_method: refundMethod || undefined,
                    refund_reference: refundReference || undefined,
                    agentId: getAgentId(),
                }),
            "Refund marked complete"
        );

    const handleCancel = () => {
        dispatch({ type: "TOGGLE_CONFIRM_CANCEL", open: false });
        submitAction(
            () =>
                cancelModify({
                    modifyTransactionId,
                    itineraryId,
                    cancelledBy: getAgentId(),
                    reason: "OPS_CANCELLED",
                }),
            "Modify cancelled"
        );
    };

    const [markFailedReason, setMarkFailedReason] = useState("");
    const handleMarkIssuanceFailed = () => {
        const reason = markFailedReason.trim();
        if (!reason) return;
        dispatch({ type: "TOGGLE_CONFIRM_MARK_FAILED", open: false });
        setMarkFailedReason("");
        submitAction(
            () =>
                markIssuanceFailed({
                    modifyTransactionId,
                    itineraryId,
                    failureReason: reason,
                    agentId: getAgentId(),
                }),
            "Marked issuance failed; refund queued"
        );
    };
    //********HANDLERS ENDS*********/

    //********EFFECTS STARTS*********/
    useEffect(() => {
        refresh();
        // Refresh only when the workflow opens; subsequent refreshes are user-driven
        // or via the opt-in auto-refresh below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modifyTransactionId]);

    // Read auto-refresh decision from sessionStorage when the modState changes.
    useEffect(() => {
        if (!snapshot) return;
        const stored = sessionStorage.getItem(autoRefreshKey(modifyTransactionId, modState));
        setAutoRefreshDecided(stored === "yes" || stored === "no" ? stored : null);
        setAutoRefreshActive(stored === "yes" && Boolean(cadence));
        setShowAutoRefreshPrompt(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modState, modifyTransactionId]);

    // After PROMPT_DELAY_MS on a poll-eligible state with no prior decision,
    // surface the inline prompt. Never silently start polling.
    useEffect(() => {
        if (promptTimerRef.current) {
            window.clearTimeout(promptTimerRef.current);
            promptTimerRef.current = null;
        }
        if (!cadence || autoRefreshDecided !== null || isTerminal) return;
        promptTimerRef.current = window.setTimeout(() => {
            setShowAutoRefreshPrompt(true);
        }, PROMPT_DELAY_MS);
        return () => {
            if (promptTimerRef.current) {
                window.clearTimeout(promptTimerRef.current);
                promptTimerRef.current = null;
            }
        };
    }, [cadence, autoRefreshDecided, isTerminal, modState]);

    // Run the actual polling interval only when ops opts in.
    useEffect(() => {
        if (pollTimerRef.current) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (!autoRefreshActive || !cadence) return;
        pollTimerRef.current = window.setInterval(() => {
            refresh();
        }, cadence * 1000);
        return () => {
            if (pollTimerRef.current) {
                window.clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [autoRefreshActive, cadence, refresh]);

    // Focus refetch for customer-action states (CUST_REVIEW, PAY_PENDING).
    useEffect(() => {
        if (!snapshot) return;
        const isCustomerActionState =
            snapshot.status === "CUST_REVIEW" || snapshot.status === "PAY_PENDING";
        if (!isCustomerActionState) return;
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [snapshot, refresh]);
    //********EFFECTS ENDS*********/

    //********AUTO-REFRESH HANDLERS STARTS*********/
    const acceptAutoRefresh = () => {
        sessionStorage.setItem(autoRefreshKey(modifyTransactionId, modState), "yes");
        setAutoRefreshDecided("yes");
        setAutoRefreshActive(true);
        setShowAutoRefreshPrompt(false);
    };
    const dismissAutoRefresh = () => {
        sessionStorage.setItem(autoRefreshKey(modifyTransactionId, modState), "no");
        setAutoRefreshDecided("no");
        setAutoRefreshActive(false);
        setShowAutoRefreshPrompt(false);
    };
    const pauseAutoRefresh = () => {
        sessionStorage.removeItem(autoRefreshKey(modifyTransactionId, modState));
        setAutoRefreshDecided(null);
        setAutoRefreshActive(false);
    };
    //********AUTO-REFRESH HANDLERS ENDS*********/

    //********RENDER STARTS*********/
    if (!snapshot && state.isFetching) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!snapshot && state.fetchError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {state.fetchError.message}
                    {state.fetchError.traceId && (
                        <Typography sx={{ fontSize: 10, mt: 1, fontFamily: "monospace" }}>
                            trace: {state.fetchError.traceId}
                        </Typography>
                    )}
                </Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
                    Back to transactions
                </Button>
            </Box>
        );
    }

    if (!snapshot) return null;

    const stepIndex = STATE_TO_STEP[modState];
    const chipPalette = STATE_CHIP_COLOR[modState];
    const supplierName = snapshot.sectors[0]?.supplierName || "supplier";

    let body: React.ReactNode = null;
    if (modState === "new_request") {
        body = (
            <StepCheckSupplier
                supplier={supplierName}
                onCheck={handleCheckAvailability}
                submitting={state.isSubmitting}
            />
        );
    } else if (modState === "checking") {
        // QUOTE_PENDING. AUTO sectors are fetched by the backend (poll); MANUAL
        // sectors need ops to enter the fare details. If any sector is MANUAL show
        // the entry/quote step so ops can fill + send; otherwise it's an AUTO poll.
        body = snapshot.sectors.some(sectorIsManual) ? (
            <StepReviewQuote snapshot={snapshot} onSend={handleSendQuote} submitting={state.isSubmitting} />
        ) : (
            <LoadingPanel
                tint="amber"
                title={`Checking with ${supplierName}...`}
                subtitle="Fetching availability and pricing. Use Refresh or auto-refresh to see live updates."
            />
        );
    } else if (modState === "quote_ready") {
        body = (
            <StepReviewQuote
                snapshot={snapshot}
                onSend={handleSendQuote}
                submitting={state.isSubmitting}
            />
        );
    } else if (modState === "sent_to_customer") {
        body = <StepSentToCustomer snapshot={snapshot} />;
    } else if (modState === "customer_approved") {
        body = (
            <StepCustomerApproved
                snapshot={snapshot}
                submitting={state.isSubmitting}
                onManualIssuance={handleManualIssuance}
                onFetchFromSupplier={handleFetchFromSupplier}
            />
        );
    } else if (modState === "customer_rejected") {
        body = <StepCustomerRejected snapshot={snapshot} onStartNew={onBack} />;
    } else if (modState === "refund_pending") {
        body = (
            <StepRefundPending
                snapshot={snapshot}
                submitting={state.isSubmitting}
                onTriggerIssuance={handleTriggerIssuance}
                onFetchFromSupplier={handleFetchFromSupplier}
                onMarkRefunded={handleMarkRefunded}
                onOpenMarkFailed={() => dispatch({ type: "TOGGLE_CONFIRM_MARK_FAILED", open: true })}
            />
        );
    } else if (modState === "refund_complete") {
        body = <StepRefundComplete snapshot={snapshot} />;
    }

    return (
        <Box sx={{ background: COLORS.bg, minHeight: "100vh", py: "20px" }}>
            <Box sx={{ maxWidth: 900, mx: "auto", px: "16px" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: "14px",
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            borderColor: COLORS.border,
                            color: COLORS.dim,
                            fontSize: 12,
                            fontWeight: 500,
                            background: COLORS.card,
                            "&:hover": { background: COLORS.lgray, borderColor: COLORS.border },
                        }}
                    >
                        Back to transactions
                    </Button>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                            Last updated {formatRelative(state.lastFetchedAt)}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                                state.isFetching ? (
                                    <CircularProgress size={12} />
                                ) : (
                                    <RefreshIcon sx={{ fontSize: 14 }} />
                                )
                            }
                            onClick={refresh}
                            disabled={state.isFetching}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                fontSize: 11,
                                borderColor: COLORS.border,
                                color: COLORS.dim,
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: "14px",
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: COLORS.accent,
                                textTransform: "uppercase",
                                letterSpacing: "1.5px",
                                mb: "2px",
                            }}
                        >
                            Modify booking workflow
                        </Typography>
                        <Typography component="h1" sx={{ fontSize: 18, fontWeight: 700 }}>
                            {customerName || snapshot.bookingId} - {snapshot.bookingId}
                        </Typography>
                    </Box>
                    <Chip
                        label={STATE_LABEL[modState]}
                        sx={{
                            background: chipPalette.bg,
                            color: chipPalette.text,
                            fontWeight: 600,
                            fontSize: 10,
                            height: 22,
                            borderRadius: 2,
                        }}
                    />
                </Box>

                <Paper
                    elevation={0}
                    sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 3, mb: "14px" }}
                >
                    <Stepper activeStep={stepIndex} />
                </Paper>

                {showAutoRefreshPrompt && cadence && (
                    <AutoRefreshPrompt
                        cadenceSeconds={cadence}
                        onAccept={acceptAutoRefresh}
                        onDismiss={dismissAutoRefresh}
                    />
                )}
                {autoRefreshActive && cadence && (
                    <ActiveAutoRefreshBar cadenceSeconds={cadence} onPause={pauseAutoRefresh} />
                )}

                <BookingDetailCard
                    snapshot={snapshot}
                    customerName={customerName}
                    customerEmail={customerEmail}
                />

                {body}

                {!isTerminal && modState !== "refund_pending" && (
                    <Box sx={{ mt: "14px", textAlign: "right" }}>
                        <Button
                            size="small"
                            onClick={() => dispatch({ type: "TOGGLE_CONFIRM_CANCEL", open: true })}
                            disabled={state.isSubmitting}
                            sx={{
                                textTransform: "none",
                                fontSize: 12,
                                color: COLORS.red,
                            }}
                        >
                            Cancel modify
                        </Button>
                    </Box>
                )}
            </Box>

            <Dialog
                open={state.confirmCancelOpen}
                onClose={() => dispatch({ type: "TOGGLE_CONFIRM_CANCEL", open: false })}
            >
                <DialogTitle>Cancel this modify request?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 13 }}>
                        {snapshot.paymentStatus === "SUCCEEDED"
                            ? "Payment has been collected. Cancelling now will trigger the refund cascade."
                            : "The customer will be notified that their request was cancelled."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch({ type: "TOGGLE_CONFIRM_CANCEL", open: false })}>
                        Keep modify
                    </Button>
                    <Button onClick={handleCancel} sx={{ color: COLORS.red }}>
                        Yes, cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={state.confirmMarkFailedOpen}
                onClose={() => {
                    dispatch({ type: "TOGGLE_CONFIRM_MARK_FAILED", open: false });
                    setMarkFailedReason("");
                }}
            >
                <DialogTitle>Mark issuance failed (fast-path)</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
                        Skip the 4h grace and go straight to REFUND_PEND. Use this only when the
                        supplier has confirmed by phone that the reissue cannot proceed. A
                        failure reason is required.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        label="Failure reason"
                        value={markFailedReason}
                        onChange={(e) => setMarkFailedReason(e.target.value)}
                        placeholder="Mondee rejected - fare class no longer available for reissue"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            dispatch({ type: "TOGGLE_CONFIRM_MARK_FAILED", open: false });
                            setMarkFailedReason("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMarkIssuanceFailed}
                        disabled={!markFailedReason.trim()}
                        sx={{ color: COLORS.red }}
                    >
                        Mark failed
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={state.toast.open}
                autoHideDuration={6000}
                onClose={() => dispatch({ type: "TOAST_CLOSE" })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={state.toast.severity}
                    onClose={() => dispatch({ type: "TOAST_CLOSE" })}
                    onClick={() => {
                        if (state.toast.traceId && navigator.clipboard) {
                            navigator.clipboard.writeText(state.toast.traceId);
                        }
                    }}
                    sx={{ cursor: state.toast.traceId ? "pointer" : "default" }}
                >
                    {state.toast.message}
                    {state.toast.traceId && (
                        <Typography
                            sx={{ fontSize: 10, mt: 0.5, fontFamily: "monospace", opacity: 0.8 }}
                        >
                            trace: {state.toast.traceId} (click to copy)
                        </Typography>
                    )}
                </Alert>
            </Snackbar>
        </Box>
    );
    //********RENDER ENDS*********/
};
//********MAIN COMPONENT ENDS*********/

export default ModifyBookingWorkflow;
